/**
 * Tables de référence : ce que l'agent IA consulte au lieu de deviner.
 * Le client crée ses colonnes (critères demandés, réponses lues), remplit des
 * lignes ; l'agent appelle un outil par table et lit la réponse du serveur.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type LookupFieldRole = 'criterion' | 'answer'
export type LookupFieldType = 'text' | 'choice' | 'number' | 'range' | 'boolean' | 'amount' | 'adresse'
/** Colonne remplie par le serveur depuis l'adresse du client, jamais demandée à l'appelant. */
export type LookupCalcul = 'distance' | 'ville'

export interface LookupOption {
  value: string
  synonyms: string[]
}

export interface LookupField {
  id?: string
  name: string
  role: LookupFieldRole
  type: LookupFieldType
  required: boolean
  hint: string
  options: LookupOption[]
  /** Colonne « Adresse du client » : d'où part le trajet (atelier, bureau). */
  depart?: string
  calcul?: LookupCalcul
}

export type LookupRowValues = Record<string, string | number | boolean | { min: number | null; max: number | null } | null>

export interface LookupRow {
  id: number
  values: LookupRowValues
  is_active: boolean
  updated_at: string
}

export interface LookupTable {
  id: number
  name: string
  slug: string
  usage_hint: string
  fields: (Required<Omit<LookupField, 'depart' | 'calcul'>> & Pick<LookupField, 'depart' | 'calcul'>)[]
  answer_template: string
  sms_enabled: boolean
  sms_template: string
  fallback_text: string
  is_active: boolean
  rows_count: number
  none_count: number
  agent_ids: number[]
  rows?: LookupRow[]
}

export interface LookupTableInput {
  name: string
  usage_hint: string
  fields: LookupField[]
  answer_template: string
  sms_enabled: boolean
  sms_template: string
  fallback_text: string
  agent_ids: number[]
  is_active?: boolean
}

export interface LookupQueryLog {
  id: number
  status: 'found' | 'missing' | 'confirm' | 'ambiguous' | 'none'
  criteria: Record<string, unknown>
  answer_text: string
  sms_sent: boolean
  call_sid: string
  created_at: string
}

export interface LookupTestResult {
  status: LookupQueryLog['status']
  message: string
  answer?: string
  sms_text?: string
  missing?: string[]
  candidates?: string[]
  ask?: string
  options?: string[]
}

const base = (companyId: number) => `/api/companies/${companyId}/lookup-tables/`

export function useLookupTables(companyId: number | undefined) {
  return useQuery({
    queryKey: ['lookup-tables', companyId],
    queryFn: async () => (await api.get<LookupTable[]>(base(companyId!))).data,
    enabled: !!companyId,
  })
}

export function useLookupTable(companyId: number | undefined, tableId: number | null) {
  return useQuery({
    queryKey: ['lookup-table', companyId, tableId],
    queryFn: async () => (await api.get<LookupTable>(`${base(companyId!)}${tableId}/`)).data,
    enabled: !!companyId && !!tableId,
  })
}

export function useSaveLookupTable(companyId: number | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id?: number; data: LookupTableInput }) =>
      (id ? await api.put<LookupTable>(`${base(companyId!)}${id}/`, data) : await api.post<LookupTable>(base(companyId!), data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lookup-tables', companyId] })
      qc.invalidateQueries({ queryKey: ['lookup-table', companyId] })
    },
  })
}

export function useDeleteLookupTable(companyId: number | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`${base(companyId!)}${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lookup-tables', companyId] }),
  })
}

export interface RowsResult {
  created: LookupRow[]
  errors: { ligne: number; error: string }[]
}

export function useAddLookupRows(companyId: number | undefined, tableId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rows: { values: LookupRowValues }[]) =>
      (await api.post<RowsResult>(`${base(companyId!)}${tableId}/rows/`, { rows })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lookup-table', companyId, tableId] })
      qc.invalidateQueries({ queryKey: ['lookup-tables', companyId] })
    },
  })
}

export function useUpdateLookupRow(companyId: number | undefined, tableId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ rowId, values }: { rowId: number; values: LookupRowValues }) =>
      (await api.put<LookupRow>(`${base(companyId!)}${tableId}/rows/${rowId}/`, { values })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lookup-table', companyId, tableId] }),
  })
}

export function useDeleteLookupRow(companyId: number | undefined, tableId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (rowId: number) => api.delete(`${base(companyId!)}${tableId}/rows/${rowId}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lookup-table', companyId, tableId] })
      qc.invalidateQueries({ queryKey: ['lookup-tables', companyId] })
    },
  })
}

export function useLookupQueries(companyId: number | undefined, tableId: number | null, status?: string) {
  return useQuery({
    queryKey: ['lookup-queries', companyId, tableId, status],
    queryFn: async () => (await api.get<LookupQueryLog[]>(`${base(companyId!)}${tableId}/queries/${status ? `?status=${status}` : ''}`)).data,
    enabled: !!companyId && !!tableId,
  })
}

export function useTestLookup(companyId: number | undefined, tableId: number | null) {
  return useMutation({
    mutationFn: async (criteria: Record<string, unknown>) =>
      (await api.post<LookupTestResult>(`${base(companyId!)}${tableId}/test/`, { criteria })).data,
  })
}
